import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    Output,
    OnChanges,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {
    FormsModule,
    ReactiveFormsModule,
    FormBuilder,
    FormGroup,
    Validators,
    AbstractControl,
    ValidationErrors
} from '@angular/forms';
import {NgbPopoverModule, NgbTooltipModule} from '@ng-bootstrap/ng-bootstrap';
import {NouisliderModule, NouisliderComponent} from 'ng2-nouislider';
import wNumb from 'wnumb';
import {
    AlertService,
    DebugLoggerService,
    HelperService,
    SubscribersService,
    WebSocketService
} from '../../../admin-core/services';
import {Constants} from '../../../admin-core/constants';
import {depositAndFinalDue, roundPrice} from '../../../admin-core/utils/quote-money.util';
import {QuoteOrderSampleService} from '../../../admin-core/apiservices';
import {CommonModule} from '@angular/common';

@Component({
    selector: 'app-slider',
    // Eager: Angular 22 defaults every component to OnPush, which would leave this view
    // unpainted after its subscribe callbacks (see app.component.ts for the full note).
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: true,
    imports: [NouisliderModule, FormsModule, ReactiveFormsModule, NgbPopoverModule, CommonModule, NgbTooltipModule],
    templateUrl: './slider.component.html',
    styleUrl: './slider.component.css'
})
export class SliderComponent implements OnChanges {

    @Input() quoteID: any;
    @Input() exportId: string = '';
    @Input() quoteSummaryID: number = 0;
    @Input() finalAmount: number = 0;
    @Input() sliderValue: number = 0;
    @Input() depositAmount: number = 0;
    @Input() finalDueAmount: number = 0;
    @Input() isDisabled: any;
    @ViewChild('slider') sliderRef: NouisliderComponent;
    sliderConfig: any;
    depositPaymentJson: any;
    oldDepositSliderValue: any;
    isLoading = false;
    sliderForm!: FormGroup;
    submitted = false;
    sliderPercentageMaxChars = 8;
    private lastEmittedValue: number = 0; // ✅ Track last emitted value to avoid redundant emissions
    private currentTooltipValue: number = 0; // ✅ Track current tooltip value for consistency

    @Output() isSliderUpdateLoadingEvent = new EventEmitter<boolean>();

    constructor(
        private alertService: AlertService,
        private orderService: QuoteOrderSampleService,
        private helperFunction: HelperService,
        private subscriberService: SubscribersService,
        private cdr: ChangeDetectorRef,
        private fb: FormBuilder,
        private webSocketService: WebSocketService,
        private debugLogger: DebugLoggerService
    ) {
    }

    ngOnInit() {
        this.oldDepositSliderValue = this.sliderValue;
        this.initializeForm();
        const self = this;

        this.sliderConfig = {
            connect: 'lower',
            start: 0,
            step: 0.00001, // ✅ 5 decimal precision needed to match final total and order summary exactly
            tooltips: {
                from(value: any): number {
                    return Number(value);
                },
                to(value: number): string {
                    return self.formatTooltipPercentage(value);
                }
            },
            range: {
                min: 0,
                max: 100,
            },
            format: {
                to: function (value: any) {
                    // ✅ Keep full precision during dragging for smooth sliding
                    return Number(value);
                },
                from: function (value: any) {
                    return Number(value);
                }
            },
            padding: [1, 0],
            pips: {
                mode: 'range',
                stepped: false, // ✅ Changed to false for smoother sliding
                density: 4, // ✅ Reduced density for better performance
                format: wNumb({
                    suffix: '%'
                })
            },
            behaviour: 'drag', // ✅ Smooth drag behavior
            // ✅ Performance optimization: update only on drag, not on every tiny movement
            // This makes sliding much smoother with fine-grained steps
            animationDuration: 0 // ✅ Disable animation for instant response
        };
    }

    /**
     * Lifecycle hook that responds to changes in input properties
     * This is triggered when the Summary API returns updated values after product changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        // Check if any of the critical slider inputs have changed
        const hasSliderValueChanged = changes['sliderValue'] && !changes['sliderValue'].firstChange;
        const hasDepositAmountChanged = changes['depositAmount'] && !changes['depositAmount'].firstChange;
        const hasFinalAmountChanged = changes['finalAmount'] && !changes['finalAmount'].firstChange;
        const hasFinalDueAmountChanged = changes['finalDueAmount'] && !changes['finalDueAmount'].firstChange;

        // If any slider-related values changed (from Summary API response)
        if (hasSliderValueChanged || hasDepositAmountChanged || hasFinalAmountChanged || hasFinalDueAmountChanged) {
            this.debugLogger.log('🔄 Slider inputs changed - updating from Summary API:', {
                sliderValue: this.sliderValue,
                depositAmount: this.depositAmount,
                finalAmount: this.finalAmount,
                finalDueAmount: this.finalDueAmount
            });

            // Keep oldDepositSliderValue in step with the SAVED percentage. When the server recomputes the
            // deposit (e.g. an SRD change moves it from 70% to 100%), a later edit must be compared with that
            // value — comparing with the slider's own last save made re-entering it say "No changes detected".
            // Only the sliderValue input counts: depositAmount also changes WHILE dragging (onPaymentChanged
            // in the parent), and taking the dragged value here would make onEndSlider skip the save.
            if (hasSliderValueChanged) {
                this.oldDepositSliderValue = Number(changes['sliderValue'].currentValue);
            }

            // Update form controls with new values from API
            if (this.sliderForm) {
                this.sliderForm.patchValue({
                    // Use parseFloat(toFixed(4)) to avoid floating-point precision issues
                    percentage: parseFloat(this.sliderValue.toFixed(4)),
                    value: this.depositAmount
                }, {emitEvent: false});
            }

            // Force slider tooltip to update by triggering a slider update
            // This ensures the tooltip recalculates with the new finalAmount
            if (this.sliderRef && this.sliderRef.slider) {
                // Get the current slider instance
                const sliderInstance = this.sliderRef.slider;

                // Update the slider value to force tooltip recalculation
                // This will trigger the tooltip formatter with the new finalAmount
                sliderInstance.set(this.sliderValue);
            }

            // Trigger change detection to update the view
            this.cdr.detectChanges();
        }
    }

    initializeForm() {
        this.sliderForm = this.fb.group({
            // Use parseFloat(toFixed(4)) to avoid floating-point precision issues like 56.15830000000001
            percentage: [parseFloat(this.sliderValue.toFixed(4)), [Validators.required, this.percentageValidator]],
            value: [this.depositAmount, [Validators.required, this.valueValidator.bind(this)]]
        });
    }

    // Custom validator for percentage (0-100)
    percentageValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value;
        if (value !== null && value !== undefined && value !== '') {
            const numValue = Number(value);
            if (isNaN(numValue) || numValue <= 0 || numValue > 100) {
                return {invalidPercentage: true};
            }
        }
        return null;
    }

    // Custom validator for value (0 to finalAmount)
    valueValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value;
        if (value !== null && value !== undefined && value !== '') {
            const numValue = Number(value);
            if (isNaN(numValue) || numValue <= 0 || numValue > this.finalAmount) {
                return {invalidValue: true};
            }
        }
        return null;
    }

    get sf() {
        return this.sliderForm.controls;
    }

    // Method to restrict input to numbers and decimal point only
    checkDecimalDot(e: any) {
        var charCode = (e.which) ? e.which : e.keyCode;
        if (charCode != 46 && charCode > 31
            && (charCode < 48 || charCode > 57))
            return false;
        return true;
    }

    // Handle update button click
    onUpdateClick(popover?: any) {
        this.submitted = true;

        // Check if form is valid
        if (this.sliderForm.invalid) {
            return;
        }

        if (Object.keys(this.depositPaymentJson).length > 0) {
            const {deposit_amount_percentage, deposit_amount, final_due_amount} = this.depositPaymentJson;

            if (deposit_amount_percentage >= 1 && deposit_amount_percentage <= 100) {
                const oldDepositPercentage = Number(this.oldDepositSliderValue);

                // Update the slider value
                this.sliderValue = +deposit_amount_percentage;
                this.depositAmount = +deposit_amount.toFixed(2);

                // NOTE: do not require oldDepositPercentage > 0 here. On a brand-new quote the
                // old value is 0, and requiring > 0 made the very first deposit set (e.g. 0 -> 70%)
                // fall through to the "No changes detected" branch and never persist. The new
                // percentage is already validated to be within 1..100 above, so a plain
                // "value actually changed" check is sufficient and correct for the first-time set.
                if (oldDepositPercentage !== Number(deposit_amount_percentage)) {
                    // this.isLoading = true;

                    // ✅ Start Loader via Event Emitter
                    this.isSliderUpdateLoadingEvent.emit(true);

                    const payload = {
                        id: this.quoteSummaryID,
                        quote_id: this.quoteID,
                        deposit_amount_percentage: Number(deposit_amount_percentage),
                        is_deposit_slider_adjust: Constants.IS_TRUE
                    };

                    this.orderService.createQuoteSummary(payload).subscribe(
                        (data) => {
                            // this.isLoading = false;
                            if (data) {
                                // ✅ Update oldDepositSliderValue after successful API call
                                this.oldDepositSliderValue = deposit_amount_percentage;

                                // Emit socket event for quote update
                                this.debugLogger.log('📡 Emitting quote_updated socket event for manual slider update');
                                this.webSocketService.emit('quote_updated', {
                                    quoteId: this.quoteID,
                                    exportId: this.exportId
                                });

                                this.alertService.success(Constants.DEPOSIT_PAYMENT_ADJUSTMENT_SUCCESS);
                                this.depositPaymentJson = {};
                                this.submitted = false;
                                this.subscriberService.triggerQuote();
                                this.closeUpdateSlider(popover); // Close popover after success
                            } else {
                                this.alertService.error(Constants.DEPOSIT_PAYMENT_ADJUSTMENT_ERROR);
                            }

                            // ✅ Stop Loader via Event Emitter
                            this.isSliderUpdateLoadingEvent.emit(false);
                        },
                        (error) => {
                            // this.isLoading = false;
                            // ✅ Stop Loader via Event Emitter
                            this.isSliderUpdateLoadingEvent.emit(false);
                            this.helperFunction.handleError(error);
                        }
                    );
                } else {
                    // No changes detected
                    this.alertService.info('No changes detected. Values are already up to date.');
                    this.closeUpdateSlider(popover); // Close popover when no changes
                }
            } else {
                this.alertService.error(Constants.DEPOSIT_PAYMENT_ADJUSTMENT_PERCENTAGE_ERROR_0);
            }
        } else {
            // No changes detected
            this.alertService.info('No changes detected. Values are already up to date.');
            this.closeUpdateSlider(popover); // Close popover when no changes
        }
    }

    formatTooltipPercentage(value: any) {
        value = Number(value);

        // ✅ Store the current tooltip value
        this.currentTooltipValue = value;

        // ✅ IMPORTANT: Use this.depositAmount directly if the slider value matches
        // This ensures tooltip shows the same value as the display, especially after API updates
        let depositPaymentAmount: number;

        if (Math.abs(this.sliderValue - value) < 0.0001) {
            // Slider value matches - use the stored depositAmount (which may come from API)
            depositPaymentAmount = this.depositAmount;
            this.debugLogger.log('formatTooltipPercentage - using stored depositAmount:', depositPaymentAmount);
        } else {
            // Slider value is different - calculate it
            depositPaymentAmount = this.calculateDepositAmount(value);
            this.debugLogger.log('formatTooltipPercentage - calculating depositAmount:', depositPaymentAmount);
        }

        // ✅ Log for debugging
        this.debugLogger.log('formatTooltipPercentage - value:', value, 'sliderValue:', this.sliderValue, 'depositAmount:', depositPaymentAmount, 'formatted:', this.formatCurrency(depositPaymentAmount));

        return `Deposit Payment: <span class="slider-tooltip-custom">$${this.formatCurrency(depositPaymentAmount)} (${value.toFixed(2)}%)</span>`;
    }

    // ✅ Centralized calculation method - single source of truth
    calculateDepositAmount(percentage: number): number {
        // Rounded exactly as the server rounds the deposit it stores (see quote-money.util)
        return depositAndFinalDue(this.finalAmount, percentage).deposit;
    }

    formatCurrency(amount: any) {
        const numericAmount: number = parseFloat(amount.toFixed(2));
        if (!isNaN(numericAmount)) {
            return numericAmount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
        } else {
            return 'Invalid amount';
        }
    }

    isDisable() {
        return false;
        // isSliderDisable || isDisabledQuoteDetail || isQuoteCancel
    }

    onSlide(event: any) {
        // ✅ Optimized for smooth sliding - minimal calculations during drag
        const value = Number(event);

        // ✅ Use centralized calculation to ensure consistency with tooltip
        // Calculate using the exact same method as the tooltip
        this.depositAmount = this.calculateDepositAmount(value);
        const total = this.finalAmount ?? 0;
        const finalDueAmount = roundPrice(total - this.depositAmount);

        // ✅ Update sliderValue to keep it in sync
        this.sliderValue = value;

        // ✅ Log for debugging
        this.debugLogger.log('onSlide - value:', value, 'depositAmount:', this.depositAmount, 'formatted:', this.formatCurrency(this.depositAmount));

        // ✅ Update form controls without triggering change detection
        // This keeps the UI in sync without causing performance issues
        this.sliderForm.get('percentage')?.setValue(value, {emitEvent: false});
        this.sliderForm.get('value')?.setValue(this.depositAmount, {emitEvent: false});

        // ✅ Emit payment changed event for parent component to update its model (only if value changed)
        if (!this.isDisabled && value !== this.lastEmittedValue) {
            this.lastEmittedValue = value;
            this.paymentChanged.emit({
                depositAmount: this.depositAmount,
                finalDueAmount: finalDueAmount
            });
        }
    }

    // rangeChanged(updatedRange: any, pop: any) {

    //   // this.sliderClass = updatedRange >= 50;
    //   // this.closeUpdateSlider(pop);

    //   // if (typeof updatedRange == "string") {
    //   //   updatedRange = Number(updatedRange);
    //   // }

    //   // updatedRange = updatedRange.toFixed(5);
    //   // let depositAmount: any = ((this.finalAmount * updatedRange) / 100);
    //   // depositAmount = Number(depositAmount)?.toFixed(2);
    //   // let finalAmount = (this.finalAmount - depositAmount);
    //   // this.depositAmount = depositAmount;
    //   // this.modelOrder.deposit_amount = depositAmount;
    //   // if (updatedRange == 100) {
    //   //   this.modelOrder.deposit_amount_percentage = 100;
    //   // } else {
    //   //   this.modelOrder.deposit_amount_percentage = updatedRange;
    //   // }
    //   // this.modelOrder.final_due_amount = finalAmount;

    //   // if (updatedRange == 100) {
    //   //   this.getScheduledPaymentDetail()
    //   // }
    // }

    onEndSlider(updatedRange: any) {
        this.debugLogger.log('🎯 onEndSlider called with updatedRange:', updatedRange, 'oldDepositSliderValue:', this.oldDepositSliderValue);
        // ✅ Removed detectChanges() here to avoid interfering with smooth sliding
        let oldDepositPercentage = Number(this.oldDepositSliderValue);
        // Do not require oldDepositPercentage > 0: the first-time drag from 0 -> N% must persist.
        // updatedRange > 0 guards against setting an empty/zero deposit, and the not-equal check
        // guards against a no-op re-save.
        if (updatedRange > 0 && oldDepositPercentage != updatedRange) {
            this.debugLogger.log('✅ Condition met - calling API to update deposit percentage');
            return new Promise((resolve, reject) => {
                // ✅ Start Loader via Event Emitter
                this.isSliderUpdateLoadingEvent.emit(true);

                let depositAmount = this.sliderValue;
                if (depositAmount > 0) {
                    depositAmount = Number(depositAmount?.toFixed(2));
                }
                let finalDueAmount = this.finalDueAmount;
                if (finalDueAmount > 0) {
                    finalDueAmount = Number(finalDueAmount?.toFixed(2));
                }

                let json = {
                    id: this.quoteSummaryID,
                    quote_id: this.quoteID,
                    deposit_amount_percentage: updatedRange,
                    is_deposit_slider_adjust: Constants.IS_TRUE,
                }

                this.orderService.createQuoteSummary(json).subscribe((data) => {
                    if (data) {
                        // ✅ Stop Loader via Event Emitter
                        this.isSliderUpdateLoadingEvent.emit(false);

                        // ✅ Update oldDepositSliderValue after successful API call
                        this.oldDepositSliderValue = updatedRange;
                        this.debugLogger.log('✅ API call successful - updated oldDepositSliderValue to:', this.oldDepositSliderValue);

                        // Emit socket event for quote update
                        this.debugLogger.log('📡 Emitting quote_updated socket event for slider change');
                        this.webSocketService.emit('quote_updated', {
                            quoteId: this.quoteID,
                            exportId: this.exportId
                        });

                        this.alertService.success(Constants.DEPOSIT_PAYMENT_ADJUSTMENT_SUCCESS);
                        // this.getQuoteDetail(false);
                        this.subscriberService.triggerQuote();
                        resolve(true);
                    } else {
                        // ✅ Stop Loader via Event Emitter
                        this.isSliderUpdateLoadingEvent.emit(false);

                        this.isLoading = false;
                        this.alertService.error(Constants.DEPOSIT_PAYMENT_ADJUSTMENT_ERROR);
                        resolve(true);
                    }
                }, (error: any) => {
                    this.isLoading = false;
                    this.helperFunction.handleError(error);
                    reject(false);
                })
            });
        } else {
            this.debugLogger.log('❌ Condition not met - skipping API call. Reasons:', {
                oldDepositPercentage,
                updatedRange,
                isOldGreaterThanZero: oldDepositPercentage > 0,
                isUpdatedGreaterThanZero: updatedRange > 0,
                areValuesDifferent: oldDepositPercentage != updatedRange
            });
        }
    }

    resetUpdateSlider(pop: any) {
        // if (this.modelOrder?.is_archive && this.modelOrder?.is_archive === Constants.IS_ARCHIVE_YES) {
        //   this.alertService.error(Constants.UNABLE_QUOTE_DETAIL_CHANGE);
        //   return false;
        // }

        // if (this.modelOrder?.is_expired && this.modelOrder?.is_expired === Constants.QUOTE_IS_EXPIRED_YES) {
        //   this.alertService.error(Constants.UNABLE_QUOTE_STATUS_CHANGE_WITH_EXPIRED);
        //   return false;
        // }

        // Reset form to current slider values when opening popover
        // Use parseFloat(toFixed(4)) to avoid floating-point precision issues like 56.15830000000001
        this.sliderForm.get('percentage')?.setValue(parseFloat(this.sliderValue.toFixed(4)));
        this.sliderForm.get('value')?.setValue(this.depositAmount);
        this.depositPaymentJson = {};
        this.submitted = false;

        pop.open();
    }

    // onFocusOutEvent(event: any, type: any, pop: any) {
    //   if (type === 'percentage') {
    //     this.depositPaymentJson = {}
    //     let depositPaymentPercentage: any = event.target.value;
    //     if (typeof depositPaymentPercentage == "string") {
    //       depositPaymentPercentage = Number(depositPaymentPercentage)
    //     }

    //     if (depositPaymentPercentage > 0 && depositPaymentPercentage <= 100) {
    //       let depositAmount: any = ((depositPaymentPercentage * this.finalAmount) / 100);
    //       depositAmount = Number(depositAmount).toFixed(2);
    //       let finalAmount = (this.finalAmount - depositAmount);
    //       this.depositAmount = depositAmount;
    //       this.depositPaymentJson = {
    //         deposit_amount: depositAmount,
    //         deposit_amount_percentage: depositPaymentPercentage,
    //         final_due_amount: finalAmount,
    //       }
    //     } else {
    //       if (depositPaymentPercentage == 0) {
    //         this.alertService.error(Constants.DEPOSIT_PAYMENT_ADJUSTMENT_PERCENTAGE_ERROR_0);
    //       } else {
    //         this.alertService.error(Constants.DEPOSIT_PAYMENT_ADJUSTMENT_PERCENTAGE_ERROR_100);
    //       }
    //     }
    //   }

    //   if (type === 'value') {
    //     this.depositPaymentJson = {}
    //     let depositPaymentvalue: any = event.target.value;
    //     if (typeof depositPaymentvalue == "string") {
    //       depositPaymentvalue = Number(depositPaymentvalue)
    //     }
    //     if (depositPaymentvalue > 0 && depositPaymentvalue <= this.finalAmount) {
    //       let depositPaymentPercentage: any = ((depositPaymentvalue * 100) / this.finalAmount);
    //       depositPaymentPercentage = Number(depositPaymentPercentage).toFixed(5);
    //       let finalAmount = (this.finalAmount - depositPaymentvalue);
    //       this.sliderValue = Number(depositPaymentPercentage).toFixed(2);
    //       this.depositPaymentJson = {
    //         deposit_amount: depositPaymentvalue,
    //         deposit_amount_percentage: depositPaymentPercentage,
    //         final_due_amount: finalAmount,
    //       }
    //     } else {
    //       if (depositPaymentvalue == 0) {
    //         this.alertService.error(Constants.DEPOSIT_PAYMENT_ADJUSTMENT_VALUE_ERROR_0);
    //       } else {
    //         this.alertService.error(Constants.DEPOSIT_PAYMENT_ADJUSTMENT_VALUE_ERROR_100);
    //       }
    //     }
    //   }

    //   if (this.depositPaymentJson !== "" && type === 'update') {
    //     if (this.depositPaymentJson.deposit_amount_percentage && this.depositPaymentJson.deposit_amount_percentage >= 1 && this.depositPaymentJson.deposit_amount_percentage <= 100) {
    //       this.closeUpdateSlider(pop);
    //       let oldDepositPercentage = Number(this.oldDepositSliderValue);
    //       if (oldDepositPercentage > 0 && this.depositPaymentJson.deposit_amount_percentage >= 1 && oldDepositPercentage != this.depositPaymentJson.deposit_amount_percentage) {
    //         return new Promise((resolve, reject) => {
    //           this.isLoading = true;
    //           let depositAmount = this.depositPaymentJson.deposit_amount;
    //           let finalDueAmount = this.depositPaymentJson.final_due_amount;

    //           let json = {
    //             id: this.modelId,
    //             deposit_amount: Number(depositAmount),
    //             deposit_amount_percentage: Number(this.depositPaymentJson.deposit_amount_percentage),
    //             final_due_amount: Number(finalDueAmount),
    //             is_deposit_slider_adjust: Constants.IS_DEPOSIT_SLIDER_ADJUST_YES,
    //           }

    //           this.orderService.updateDepositPayment(json).subscribe((data) => {
    //             if (data) {
    //               this.isLoading = false
    //               this.alertService.success(Constants.DEPOSIT_PAYMENT_ADJUSTMENT_SUCCESS);
    //               this.depositPaymentJson = {};
    //               // if (this.depositPaymentJson.deposit_amount_percentage == 100) {
    //               //   this.getScheduledPaymentDetail()
    //               // }
    //               // this.getQuoteDetail(false);
    //               resolve(true);
    //             } else {
    //               this.alertService.error(Constants.DEPOSIT_PAYMENT_ADJUSTMENT_ERROR);
    //               resolve(true);
    //             }
    //           }, (error: any) => {
    //             this.helperFunction.handleError(error);
    //             reject(false);
    //           })
    //         });
    //       }
    //     } else {
    //       this.closeUpdateSlider(pop);
    //       if (this.depositPaymentJson.deposit_amount_percentage < 1) {
    //         this.alertService.error(Constants.DEPOSIT_PAYMENT_ADJUSTMENT_PERCENTAGE_ERROR_0);
    //       }
    //     }
    //   }
    // }

    // onFocusOutEvent(event: any, type: any, pop: any) {
    //   if (type === 'percentage') {
    //     this.depositPaymentJson = {};
    //     let depositPaymentPercentage = Number(event.target.value);

    //     if (depositPaymentPercentage > 0 && depositPaymentPercentage <= 100) {
    //       const depositAmount = Number(((depositPaymentPercentage * this.finalAmount) / 100).toFixed(2));
    //       const finalAmount = this.finalAmount - depositAmount;

    //       this.depositAmount = depositAmount;
    //       this.sliderValue = +depositPaymentPercentage.toFixed(2);

    //       this.depositPaymentJson = {
    //         deposit_amount: depositAmount,
    //         deposit_amount_percentage: depositPaymentPercentage,
    //         final_due_amount: finalAmount
    //       };
    //     } else {
    //       this.alertService.error(
    //         depositPaymentPercentage === 0
    //           ? Constants.DEPOSIT_PAYMENT_ADJUSTMENT_PERCENTAGE_ERROR_0
    //           : Constants.DEPOSIT_PAYMENT_ADJUSTMENT_PERCENTAGE_ERROR_100
    //       );
    //     }
    //   }

    //   if (type === 'value') {
    //     this.depositPaymentJson = {};
    //     let depositPaymentValue = Number(event.target.value);

    //     if (depositPaymentValue > 0 && depositPaymentValue <= this.finalAmount) {
    //       const depositPaymentPercentage = Number(((depositPaymentValue * 100) / this.finalAmount));
    //       const finalAmount = this.finalAmount - depositPaymentValue;


    //       this.depositAmount = depositPaymentValue;
    //       this.sliderValue = +depositPaymentPercentage.toFixed(2);

    //       this.depositPaymentJson = {
    //         deposit_amount: depositPaymentValue,
    //         deposit_amount_percentage: depositPaymentPercentage,
    //         final_due_amount: finalAmount
    //       };
    //     } else {
    //       this.alertService.error(
    //         depositPaymentValue === 0
    //           ? Constants.DEPOSIT_PAYMENT_ADJUSTMENT_VALUE_ERROR_0
    //           : Constants.DEPOSIT_PAYMENT_ADJUSTMENT_VALUE_ERROR_100
    //       );
    //     }
    //   }

    //   if (type === 'update' && Object.keys(this.depositPaymentJson).length > 0) {
    //     const { deposit_amount_percentage, deposit_amount, final_due_amount } = this.depositPaymentJson;

    //     if (deposit_amount_percentage >= 1 && deposit_amount_percentage <= 100) {
    //       const oldDepositPercentage = Number(this.oldDepositSliderValue);

    //       // Update frontend value regardless of API result
    //       this.depositAmount = +deposit_amount.toFixed(2);
    //       this.sliderValue = +deposit_amount_percentage.toFixed(5);

    //       this.closeUpdateSlider(pop);

    //       if (oldDepositPercentage > 0 && oldDepositPercentage !== Number(deposit_amount_percentage)) {
    //         this.isLoading = true;

    //         const payload = {
    //           id: this.quoteSummaryID,
    //           quote_id: this.quoteID,
    //           deposit_amount_percentage: Number(deposit_amount_percentage),
    //           is_deposit_slider_adjust: Constants.IS_TRUE
    //         };

    //         this.orderService.createQuoteSummary(payload).subscribe(
    //           (data) => {
    //             this.isLoading = false;
    //             if (data) {
    //               this.alertService.success(Constants.DEPOSIT_PAYMENT_ADJUSTMENT_SUCCESS);
    //               this.depositPaymentJson = {};
    //               // Optional: refresh quote details or scheduled payment
    //               // this.getScheduledPaymentDetail();
    //               // this.getQuoteDetail(false);
    //             } else {
    //               this.alertService.error(Constants.DEPOSIT_PAYMENT_ADJUSTMENT_ERROR);
    //             }
    //           },
    //           (error) => {
    //             this.isLoading = false;
    //             this.helperFunction.handleError(error);
    //           }
    //         );
    //       }
    //     } else {
    //       this.closeUpdateSlider(pop);
    //       this.alertService.error(Constants.DEPOSIT_PAYMENT_ADJUSTMENT_PERCENTAGE_ERROR_0);
    //     }
    //   }
    // }

    // Handle input changes - only update the corresponding form field, NOT the slider
    onInputChange(type: 'percentage' | 'value') {
        if (type === 'percentage') {
            const percentageValue = this.sliderForm.get('percentage')?.value;
            if (percentageValue && percentageValue > 0 && percentageValue <= 100) {
                const depositAmount = depositAndFinalDue(this.finalAmount, percentageValue).deposit;

                // Only update the value form control, NOT the slider
                this.sliderForm.get('value')?.setValue(depositAmount, {emitEvent: false});
            }
        }

        if (type === 'value') {
            const valueAmount = this.sliderForm.get('value')?.value;
            if (valueAmount && valueAmount > 0 && valueAmount <= this.finalAmount) {
                const depositPaymentPercentage = Number(((valueAmount * 100) / this.finalAmount));

                // Only update the percentage form control, NOT the slider
                this.sliderForm.get('percentage')?.setValue(depositPaymentPercentage.toFixed(2), {emitEvent: false});
            }
        }
    }

    onFocusOutEvent(event: any, type: any) {
        // ✅ Removed detectChanges() to improve performance

        if (type === 'percentage') {
            this.depositPaymentJson = {};
            let depositPaymentPercentage = Number(event.target.value);

            // Update form control
            this.sliderForm.get('percentage')?.setValue(depositPaymentPercentage);
            this.sliderForm.get('percentage')?.markAsTouched();

            if (depositPaymentPercentage > 0 && depositPaymentPercentage <= 100) {
                const {deposit: depositAmount, finalDue: finalAmount} = depositAndFinalDue(this.finalAmount, depositPaymentPercentage);

                // DO NOT update slider value here - only prepare data for update
                // Update value form control
                this.sliderForm.get('value')?.setValue(depositAmount);

                this.depositPaymentJson = {
                    deposit_amount: depositAmount,
                    deposit_amount_percentage: depositPaymentPercentage,
                    final_due_amount: finalAmount
                };
            } else {
                this.alertService.error(
                    depositPaymentPercentage === 0
                        ? Constants.DEPOSIT_PAYMENT_ADJUSTMENT_PERCENTAGE_ERROR_0
                        : Constants.DEPOSIT_PAYMENT_ADJUSTMENT_PERCENTAGE_ERROR_100
                );
            }
        }

        if (type === 'value') {
            this.depositPaymentJson = {};
            let depositPaymentValue = Number(event.target.value);

            // Update form control
            this.sliderForm.get('value')?.setValue(depositPaymentValue);
            this.sliderForm.get('value')?.markAsTouched();

            if (depositPaymentValue > 0 && depositPaymentValue <= this.finalAmount) {
                const depositPaymentPercentage = Number(((depositPaymentValue * 100) / this.finalAmount)).toFixed(4);
                const finalAmount = roundPrice(this.finalAmount - depositPaymentValue);

                // DO NOT update slider value here - only prepare data for update
                // Update percentage form control
                this.sliderForm.get('percentage')?.setValue(depositPaymentPercentage);

                this.depositPaymentJson = {
                    deposit_amount: depositPaymentValue,
                    deposit_amount_percentage: depositPaymentPercentage,
                    final_due_amount: finalAmount
                };
            } else {
                this.alertService.error(
                    depositPaymentValue === 0
                        ? Constants.DEPOSIT_PAYMENT_ADJUSTMENT_VALUE_ERROR_0
                        : Constants.DEPOSIT_PAYMENT_ADJUSTMENT_VALUE_ERROR_100
                );
            }
        }


    }


    closeUpdateSlider(pop: any) {
        // Reset form to current slider values when closing without saving
        // Use parseFloat(toFixed(4)) to avoid floating-point precision issues like 56.15830000000001
        this.sliderForm.get('percentage')?.setValue(parseFloat(this.sliderValue.toFixed(4)));
        this.sliderForm.get('value')?.setValue(this.depositAmount);
        this.depositPaymentJson = {};
        this.submitted = false;

        if (pop) {
            pop.close();
        }
    }

    @Output() paymentChanged = new EventEmitter<{ depositAmount: number; finalDueAmount: number }>();
}
